import { MotionConfig } from 'motion/react';
import { lazy, Suspense, useCallback, useState, type ReactNode } from 'react';
import { today } from './app/data.ts';
import { I18nProvider, useT } from './app/i18n.tsx';
import { useRoute } from './app/router.tsx';
import { AuthProvider, SessionProvider, useAuth, useSession } from './app/session.tsx';
import { ThemeProvider } from './app/theme.tsx';
import { DashboardPage } from './pages/Dashboard.tsx';
import { LoginPage } from './pages/Login.tsx';
import { QuickAdd, type QuickAddKind } from './pages/QuickAdd.tsx';
import { Shell } from './shell/Shell.tsx';
import { Skeleton, ToastProvider } from './ui/primitives.tsx';

// Keep the dashboard ready at sign-in; other screens load only when they are opened.
const AccountsPage = lazy(() => import('./pages/Accounts.tsx').then((page) => ({ default: page.AccountsPage })));
const BudgetsPage = lazy(() => import('./pages/Budgets.tsx').then((page) => ({ default: page.BudgetsPage })));
const LoanPage = lazy(() => import('./pages/Loan.tsx').then((page) => ({ default: page.LoanPage })));
const LoansPage = lazy(() => import('./pages/Loans.tsx').then((page) => ({ default: page.LoansPage })));
const NetWorthPage = lazy(() => import('./pages/NetWorth.tsx').then((page) => ({ default: page.NetWorthPage })));
const OnboardingPage = lazy(() => import('./pages/Onboarding.tsx').then((page) => ({ default: page.OnboardingPage })));
const PeoplePage = lazy(() => import('./pages/People.tsx').then((page) => ({ default: page.PeoplePage })));
const RecurringPage = lazy(() => import('./pages/Recurring.tsx').then((page) => ({ default: page.RecurringPage })));
const SavingsPage = lazy(() => import('./pages/Savings.tsx').then((page) => ({ default: page.SavingsPage })));
const CalculatorsPage = lazy(() => import('./pages/Calculators.tsx').then((page) => ({ default: page.CalculatorsPage })));
const SettingsPage = lazy(() => import('./pages/Settings.tsx').then((page) => ({ default: page.SettingsPage })));
const SettlementsPage = lazy(() => import('./pages/Settlements.tsx').then((page) => ({ default: page.SettlementsPage })));
const TransactionsPage = lazy(() => import('./pages/Transactions.tsx').then((page) => ({ default: page.TransactionsPage })));

function Splash() {
  const t = useT();
  return <div className="splash">{t.t('common.loading')}</div>;
}

function PageLoading() {
  const t = useT();
  return (
    <div className="stack gap-4" role="status">
      <span className="text-muted">{t.t('common.loading')}</span>
      <Skeleton width="40%" height={28} />
      <Skeleton height={240} />
    </div>
  );
}

function Workspace() {
  const t = useT();
  const route = useRoute();
  const { householdId } = useSession();
  const [quickAdd, setQuickAdd] = useState<{ kind?: QuickAddKind } | null>(null);
  const openQuickAdd = useCallback((kind?: QuickAddKind) => setQuickAdd({ kind }), []);
  if (!householdId) {
    return (
      <Suspense fallback={<Splash />}>
        <OnboardingPage />
      </Suspense>
    );
  }

  let title: string;
  let page: ReactNode;
  switch (route.page) {
    case 'dashboard':
      title = t.t('dashboard.title');
      page = <DashboardPage onQuickAdd={openQuickAdd} />;
      break;
    case 'transactions':
      title = t.t('transactions.title');
      page = <TransactionsPage route={route} onQuickAdd={openQuickAdd} />;
      break;
    case 'recurring':
      title = t.t('recurring.title');
      page = <RecurringPage />;
      break;
    case 'loans':
      title = t.t('nav.loans');
      page = <LoansPage />;
      break;
    case 'loan':
      title = t.t('nav.loan');
      page = <LoanPage id={route.id ?? ''} />;
      break;
    case 'budgets':
      title = t.t('nav.budgets');
      page = <BudgetsPage />;
      break;
    case 'accounts':
      title = t.t('accounts.title');
      page = <AccountsPage />;
      break;
    case 'settlements':
      title = t.t('settlement.title');
      page = <SettlementsPage />;
      break;
    case 'netWorth':
      title = t.t('netWorth.title');
      page = <NetWorthPage />;
      break;
    case 'people':
      title = t.t('people.title');
      page = <PeoplePage />;
      break;
    case 'savings':
      title = t.t('savings.title');
      page = <SavingsPage />;
      break;
    case 'simulation':
      title = t.t('nav.simulation');
      page = <CalculatorsPage />;
      break;
    case 'settings':
      title = t.t('settings.title');
      page = <SettingsPage route={route} />;
      break;
  }

  return (
    <>
      <Shell route={route} title={title} showPeriod={route.page === 'dashboard' || (route.page === 'transactions' && route.view !== 'upcoming') || route.page === 'recurring' || route.page === 'budgets' || route.page === 'settlements'} onQuickAdd={openQuickAdd} today={today()}>
        <Suspense fallback={<PageLoading />}>{page}</Suspense>
      </Shell>
      <QuickAdd open={quickAdd !== null} initialKind={quickAdd?.kind} onClose={() => setQuickAdd(null)} />
    </>
  );
}

function Gate() {
  const { state } = useAuth();
  if (state.status === 'loading') return <Splash />;
  if (state.status === 'signedOut') return <LoginPage />;
  return (
    <SessionProvider key={state.session.user.id} userId={state.session.user.id} fallback={<Splash />}>
      <Workspace />
    </SessionProvider>
  );
}

export function App() {
  return (
    // Motion follows the system's "reduce motion" setting everywhere.
    <MotionConfig reducedMotion="user">
      <I18nProvider>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <Gate />
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </I18nProvider>
    </MotionConfig>
  );
}
